import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress
} from '@mui/material';
import api from '../../utils/api';

const AddBranchDialog = ({ open, onClose, onSuccess, isFirstBranch = false }) => {
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    phone: '',
    email: '',
    isMainBranch: isFirstBranch
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCheckboxChange = (e) => {
    setFormData(prev => ({
      ...prev,
      isMainBranch: e.target.checked
    }));
  };

  const validateForm = () => {
    if (!formData.name.trim()) return 'Branch name is required';
    if (!formData.address.trim()) return 'Address is required';
    if (!formData.phone.trim()) return 'Phone number is required';
    if (!formData.email.trim()) return 'Email is required';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) return 'Invalid email format';
    if (!/^\+?[\d\s-]+$/.test(formData.phone)) return 'Invalid phone number format';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/branches', formData);
      onSuccess(response.data.branch);
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create branch');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={loading ? undefined : onClose}
      maxWidth="sm" 
      fullWidth
    >
      <DialogTitle>
        {isFirstBranch ? 'Create Your Main Branch' : 'Add New Branch'}
      </DialogTitle>
      
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {isFirstBranch && (
            <Alert severity="info" sx={{ mb: 2 }}>
              This will be your main branch. You can add more branches later.
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              autoFocus
              name="name"
              label="Branch Name"
              fullWidth
              required
              value={formData.name}
              onChange={handleChange}
              disabled={loading}
            />

            <TextField
              name="address"
              label="Physical Address"
              fullWidth
              required
              multiline
              rows={2}
              value={formData.address}
              onChange={handleChange}
              disabled={loading}
            />

            <TextField
              name="phone"
              label="Phone Number"
              fullWidth
              required
              value={formData.phone}
              onChange={handleChange}
              disabled={loading}
              helperText="Include country code (e.g., +254...)"
            />

            <TextField
              name="email"
              label="Branch Email"
              type="email"
              fullWidth
              required
              value={formData.email}
              onChange={handleChange}
              disabled={loading}
            />

            {!isFirstBranch && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.isMainBranch}
                    onChange={handleCheckboxChange}
                    disabled={loading}
                  />
                }
                label="Set as main branch"
              />
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={onClose} 
            disabled={loading}
            sx={{ mr: 1 }}
          >
            Cancel
          </Button>
          <Button 
            type="submit"
            variant="contained"
            disabled={loading}
            startIcon={loading && <CircularProgress size={20} />}
          >
            {loading ? 'Creating...' : 'Create Branch'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AddBranchDialog;